/**
 * Per-country market configuration — frontend mirror of backend
 * `market_config.py`. Single source of truth for currency, VAT rate and number
 * locale per home country. Keep the table in lock-step with the backend; a
 * tenant lives entirely in one home market (no FX).
 *
 * Consumed by the tenant-market hook (`useMarket`) and the shared `formatMoney`
 * money formatter used across the portal.
 */

// country (ISO 3166-1 alpha-2, lowercase) → [currency, vat_rate, number_locale]
const MARKET: Record<string, [currency: string, vatRate: number, locale: string]> = {
  nl: ['EUR', 0.21, 'nl-NL'],
  de: ['EUR', 0.19, 'de-DE'],
  be: ['EUR', 0.21, 'nl-BE'],
  fr: ['EUR', 0.2, 'fr-FR'],
  pl: ['PLN', 0.23, 'pl-PL'],
  se: ['SEK', 0.25, 'sv-SE'],
  no: ['NOK', 0.25, 'nb-NO'],
  fi: ['EUR', 0.255, 'fi-FI'],
  gb: ['GBP', 0.2, 'en-GB'],
  ie: ['EUR', 0.23, 'en-IE'],
  at: ['EUR', 0.2, 'de-AT'],
  ch: ['CHF', 0.081, 'de-CH'],
  lu: ['EUR', 0.17, 'fr-LU'],
  es: ['EUR', 0.21, 'es-ES'],
  it: ['EUR', 0.22, 'it-IT'],
  pt: ['EUR', 0.23, 'pt-PT'],
  dk: ['DKK', 0.25, 'da-DK'],
  cz: ['CZK', 0.21, 'cs-CZ'],
};

function row(country: string | null | undefined): [string, number, string] | undefined {
  return MARKET[(country ?? '').toLowerCase().trim()];
}

export function currencyFor(country: string | null | undefined): string {
  return row(country)?.[0] ?? 'EUR';
}

export function vatRateFor(country: string | null | undefined): number {
  return row(country)?.[1] ?? 0;
}

export function localeFor(country: string | null | undefined): string {
  return row(country)?.[2] ?? 'en-GB';
}

/**
 * Format a price (in minor units / cents) as a localized currency string.
 * Drives all money rendering in the portal so a SE tenant gets "1 234,00 kr"
 * and an NL tenant "€ 549,00" from the same call. Falls back to a bare
 * "<currency> <amount>" if Intl can't handle the currency/locale pair.
 */
export function formatMoney(cents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${currency} ${(cents / 100).toFixed(2)}`;
  }
}

/** Display preference for whether prices show with or without VAT. */
export type VatMode = 'incl' | 'excl';

/**
 * Apply a VAT-display preference to a stored (incl-VAT) price.
 *
 * Stored prices and all comparison math stay incl-VAT — this is a pure
 * format-time transform. In `incl` mode the value is returned unchanged; in
 * `excl` mode the VAT is stripped: `cents / (1 + vatRate)`, rounded to whole
 * cents. A `vatRate` of 0 is a no-op in either mode.
 */
export function applyVat(cents: number, mode: VatMode, vatRate: number): number {
  if (mode === 'incl' || vatRate <= 0) return cents;
  return Math.round(cents / (1 + vatRate));
}
